<!-- FieldResourcesPanel.vue - Panel de gestión de recursos -->
<template>
  <div class="field-resources-panel">
    <!-- Resumen de recursos -->
    <v-row class="mb-4">
      <v-col cols="4">
        <v-card class="text-center pa-2" color="success" variant="outlined">
          <v-card-text>
            <v-icon size="24" class="mb-1">mdi-package-check</v-icon>
            <div class="text-h6">{{ availableResources }}</div>
            <div class="text-caption">Disponibles</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="4">
        <v-card class="text-center pa-2" color="warning" variant="outlined">
          <v-card-text>
            <v-icon size="24" class="mb-1">mdi-alert-outline</v-icon>
            <div class="text-h6">{{ lowStockResources }}</div>
            <div class="text-caption">Stock bajo</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="4">
        <v-card class="text-center pa-2" color="error" variant="outlined">
          <v-card-text>
            <v-icon size="24" class="mb-1">mdi-package-variant-remove</v-icon>
            <div class="text-h6">{{ outOfStockResources }}</div>
            <div class="text-caption">Sin stock</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filtros y búsqueda -->
    <v-card class="mb-4" variant="outlined">
      <v-card-text class="pb-2">
        <v-row>
          <v-col cols="6">
            <v-text-field
              v-model="searchQuery"
              density="compact"
              placeholder="Buscar recurso..."
              prepend-inner-icon="mdi-magnify"
              hide-details
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="categoryFilter"
              :items="categories"
              density="compact"
              placeholder="Filtrar por categoría"
              hide-details
              clearable
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Lista de recursos -->
    <div class="resources-list">
      <v-card
        v-for="resource in filteredResources"
        :key="resource.id"
        class="mb-3"
        :class="getResourceClass(resource)"
        variant="outlined"
      >
        <v-card-title class="d-flex align-center">
          <v-avatar :color="getResourceCategoryColor(resource.category)" size="32" class="mr-3">
            <v-icon color="white">{{ getResourceCategoryIcon(resource.category) }}</v-icon>
          </v-avatar>
          {{ resource.name }}
          <v-spacer></v-spacer>
          <v-chip
            :color="getStockStatusColor(resource)"
            size="small"
            variant="flat"
          >
            {{ resource.current_quantity }} / {{ resource.max_quantity }}
          </v-chip>
        </v-card-title>
        
        <v-card-text>
          <!-- Información básica -->
          <div class="mb-3">
            <div class="d-flex justify-space-between mb-2">
              <div>
                <v-chip
                  size="small"
                  :color="getResourceCategoryColor(resource.category)"
                  variant="outlined"
                >
                  {{ resource.category }}
                </v-chip>
                <v-chip
                  size="small"
                  class="ml-2"
                  color="primary"
                  variant="outlined"
                >
                  {{ resource.unit }}
                </v-chip>
              </div>
              <div class="text-caption text-medium-emphasis">
                ID: {{ resource.id }}
              </div>
            </div>
          </div>

          <!-- Descripción -->
          <div class="mb-3" v-if="resource.description">
            <div class="text-caption text-medium-emphasis">Descripción</div>
            <div class="text-body-2">{{ resource.description }}</div>
          </div>

          <!-- Nivel de stock -->
          <div class="mb-3">
            <div class="d-flex justify-space-between mb-1">
              <span class="text-caption">Nivel de stock</span>
              <span class="text-caption">{{ getStockPercentage(resource) }}%</span>
            </div>
            <v-progress-linear
              :model-value="getStockPercentage(resource)"
              :color="getStockStatusColor(resource)"
              height="8"
              rounded
            ></v-progress-linear>
            <div class="d-flex justify-space-between text-caption mt-1">
              <span>Mín: {{ resource.min_quantity }}</span>
              <span>Máx: {{ resource.max_quantity }}</span>
            </div>
          </div>

          <!-- Ubicación -->
          <div class="mb-3" v-if="resource.location">
            <div class="text-caption text-medium-emphasis mb-1">Ubicación</div>
            <div class="d-flex align-center">
              <v-icon size="16" class="mr-1">mdi-map-marker</v-icon>
              <span class="text-body-2">{{ resource.location.warehouse }} - {{ resource.location.section }}</span>
            </div>
          </div>

          <!-- Estado y disponibilidad -->
          <div class="mb-3">
            <div class="d-flex justify-space-between">
              <div>
                <div class="text-caption text-medium-emphasis">Estado</div>
                <v-chip
                  size="small"
                  :color="getResourceStatusColor(resource.status)"
                  :prepend-icon="getResourceStatusIcon(resource.status)"
                >
                  {{ getResourceStatusText(resource.status) }}
                </v-chip>
              </div>
              <div class="text-right">
                <div class="text-caption text-medium-emphasis">Último uso</div>
                <div class="text-body-2">{{ formatDate(resource.last_used) }}</div>
              </div>
            </div>
          </div>

          <!-- Reservas activas -->
          <div v-if="resource.reservations?.length" class="mb-3">
            <div class="text-caption text-medium-emphasis mb-2">
              Reservas activas ({{ resource.reservations.length }})
            </div>
            <div class="reservations-list">
              <v-chip
                v-for="reservation in resource.reservations"
                :key="reservation.id"
                size="small"
                color="info"
                class="mr-1 mb-1"
              >
                <v-icon start size="16">mdi-account</v-icon>
                {{ reservation.reserved_by }} ({{ reservation.quantity }})
              </v-chip>
            </div>
          </div>

          <!-- Historial de movimientos recientes -->
          <div v-if="resource.recent_movements?.length" class="mb-2">
            <div class="text-caption text-medium-emphasis mb-2">
              Movimientos recientes
            </div>
            <div class="movements-list">
              <div
                v-for="movement in resource.recent_movements.slice(0, 2)"
                :key="movement.id"
                class="d-flex justify-space-between align-center mb-1"
              >
                <div class="d-flex align-center">
                  <v-icon
                    size="16"
                    :color="getMovementColor(movement.type)"
                    class="mr-2"
                  >
                    {{ getMovementIcon(movement.type) }}
                  </v-icon>
                  <span class="text-body-2">{{ movement.type }} ({{ movement.quantity }})</span>
                </div>
                <span class="text-caption">{{ formatDate(movement.date) }}</span>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-btn
            v-if="resource.status === 'available' && resource.current_quantity > 0"
            @click="requestResource(resource)"
            color="primary"
            size="small"
            prepend-icon="mdi-package-down"
          >
            Solicitar
          </v-btn>
          
          <v-btn
            @click="transferResource(resource)"
            color="info"
            size="small"
            prepend-icon="mdi-swap-horizontal"
          >
            Transferir
          </v-btn>
          
          <v-btn
            v-if="resource.current_quantity <= resource.min_quantity"
            @click="restockResource(resource)"
            color="warning"
            size="small"
            prepend-icon="mdi-package-up"
          >
            Reabastecer
          </v-btn>

          <v-spacer></v-spacer>
          
          <v-btn
            icon="mdi-history"
            size="small"
            variant="text"
            @click="viewResourceHistory(resource)"
          ></v-btn>
          
          <v-btn
            icon="mdi-pencil"
            size="small"
            variant="text"
            @click="editResource(resource)"
          ></v-btn>
        </v-card-actions>
      </v-card>
    </div>

    <!-- Botón de reabastecimiento de emergencia -->
    <v-btn
      block
      color="error"
      prepend-icon="mdi-truck-fast"
      class="mt-4"
      @click="$emit('emergency-restock')"
    >
      Reabastecimiento de Emergencia
    </v-btn>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  resources: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits([
  'request-resource',
  'transfer-resource',
  'restock-resource',
  'emergency-restock'
])

// Reactive data
const searchQuery = ref('')
const categoryFilter = ref(null)

// Computed properties
const categories = computed(() => {
  const cats = [...new Set(props.resources.map(r => r.category))]
  return cats.map(cat => ({ title: cat, value: cat }))
})

const filteredResources = computed(() => {
  let filtered = props.resources

  if (searchQuery.value) {
    filtered = filtered.filter(resource =>
      resource.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      resource.description?.toLowerCase().includes(searchQuery.value.toLowerCase())
    )
  }

  if (categoryFilter.value) {
    filtered = filtered.filter(resource => resource.category === categoryFilter.value)
  }

  return filtered
})

const availableResources = computed(() =>
  props.resources.filter(r => r.status === 'available' && r.current_quantity > 0).length
)

const lowStockResources = computed(() =>
  props.resources.filter(r => 
    r.current_quantity > 0 && r.current_quantity <= r.min_quantity
  ).length
)

const outOfStockResources = computed(() =>
  props.resources.filter(r => r.current_quantity === 0).length
)

// Métodos
const getStockPercentage = (resource) => {
  return Math.round((resource.current_quantity / resource.max_quantity) * 100)
}

const getStockStatusColor = (resource) => {
  const percentage = getStockPercentage(resource)
  if (percentage === 0) return 'error'
  if (percentage <= 25) return 'warning'
  if (percentage <= 50) return 'info'
  return 'success'
}

const getResourceCategoryColor = (category) => {
  const colors = {
    'Instrumentos': 'primary',
    'Materiales': 'info',
    'Herramientas': 'warning',
    'Equipos': 'success',
    'Consumibles': 'purple',
    'Repuestos': 'orange'
  }
  return colors[category] || 'grey'
}

const getResourceCategoryIcon = (category) => {
  const icons = {
    'Instrumentos': 'mdi-gauge',
    'Materiales': 'mdi-package-variant',
    'Herramientas': 'mdi-wrench',
    'Equipos': 'mdi-tools',
    'Consumibles': 'mdi-bottle-tonic',
    'Repuestos': 'mdi-cog'
  }
  return icons[category] || 'mdi-package'
}

const getResourceStatusColor = (status) => {
  const colors = {
    available: 'success',
    reserved: 'info',
    maintenance: 'warning',
    damaged: 'error',
    out_of_stock: 'grey'
  }
  return colors[status] || 'grey'
}

const getResourceStatusIcon = (status) => {
  const icons = {
    available: 'mdi-check-circle',
    reserved: 'mdi-clock-outline',
    maintenance: 'mdi-wrench',
    damaged: 'mdi-alert-circle',
    out_of_stock: 'mdi-package-variant-remove'
  }
  return icons[status] || 'mdi-help-circle'
}

const getResourceStatusText = (status) => {
  const texts = {
    available: 'Disponible',
    reserved: 'Reservado',
    maintenance: 'Mantenimiento',
    damaged: 'Dañado',
    out_of_stock: 'Sin stock'
  }
  return texts[status] || status
}

const getResourceClass = (resource) => {
  const classes = ['resource-card']
  
  if (resource.current_quantity === 0) {
    classes.push('resource-out-of-stock')
  } else if (resource.current_quantity <= resource.min_quantity) {
    classes.push('resource-low-stock')
  }
  
  return classes.join(' ')
}

const getMovementColor = (type) => {
  const colors = {
    entrada: 'success',
    salida: 'info',
    transferencia: 'warning',
    devolucion: 'purple'
  }
  return colors[type] || 'grey'
}

const getMovementIcon = (type) => {
  const icons = {
    entrada: 'mdi-arrow-down',
    salida: 'mdi-arrow-up',
    transferencia: 'mdi-swap-horizontal',
    devolucion: 'mdi-undo'
  }
  return icons[type] || 'mdi-swap-vertical'
}

const formatDate = (date) => {
  if (!date) return 'N/A'
  return new Date(date).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const requestResource = (resource) => {
  console.log('📦 Solicitar recurso:', resource.name)
  emit('request-resource', resource.id)
}

const transferResource = (resource) => {
  console.log('🔄 Transferir recurso:', resource.name)
  emit('transfer-resource', resource.id)
}

const restockResource = (resource) => {
  console.log('📈 Reabastecer recurso:', resource.name)
  emit('restock-resource', resource.id)
}

const viewResourceHistory = (resource) => {
  console.log('📋 Ver historial del recurso:', resource.name)
  // Implementar vista de historial
}

const editResource = (resource) => {
  console.log('✏️ Editar recurso:', resource.name)
  // Implementar edición
}
</script>

<style scoped>
.field-resources-panel {
  height: 100%;
}

.resources-list {
  max-height: 500px;
  overflow-y: auto;
}

.resource-card {
  transition: all 0.3s ease;
}

.resource-out-of-stock {
  background: rgba(244, 67, 54, 0.05);
  border-left: 3px solid #f44336;
}

.resource-low-stock {
  background: rgba(255, 152, 0, 0.05);
  border-left: 3px solid #ff9800;
}

.resource-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.reservations-list,
.movements-list {
  max-height: 100px;
  overflow-y: auto;
}
</style>
